import { Climate } from '../../../../../../constants/Climate';
import { Crop, CropType } from '../../../../../../constants/CropConstants';
import { SeedbedQuality } from '../../../../../../constants/SeedbedQuality';
import { SoilType } from '../../../../../../constants/SoilType';
import { Identifier } from '../../../../SeedingCalculator';
import { SeedRateResult } from '../../../../DanishSeedingModelV1SeedRate';
import { isCropHybrid } from '../SeedingCalcUtils';

export interface CalculatePrescriptionLimitParams {
  selectedCrop: Crop;
  organic: boolean;
  TGW: number | null;
  germinationPercentage: number | null;
  unitsPerSack: number | null;
  sackWeight: number | null;
  seedbedQuality: SeedbedQuality;
  seedingDate: Date | null;
  soilType: SoilType;
  climate: Climate;
}

export interface CalculatePrescriptionLimitOptions {
  allowDateOutsideRange?: boolean;
}

export type SuccessResult = { ok: true; value: number };
export type ErrorResult = { ok: false; reason: string };
export type Result = SuccessResult | ErrorResult;

const requiredForHybrid: (keyof CalculatePrescriptionLimitParams)[] = [
  'selectedCrop',
  'organic',
  'unitsPerSack',
  'sackWeight',
  'seedbedQuality',
  'seedingDate',
  'soilType',
];
const requiredForNonHybrid: (keyof CalculatePrescriptionLimitParams)[] = [
  'organic',
  'TGW',
  'germinationPercentage',
  'seedbedQuality',
  'soilType',
];
const positiveForHybrid: (keyof CalculatePrescriptionLimitParams)[] = [
  'unitsPerSack',
  'sackWeight',
];
const positiveForNonHybrid: (keyof CalculatePrescriptionLimitParams)[] = [
  'TGW',
  'germinationPercentage',
];

export class SeedingCalculatorStrategy {
  identifier: Identifier | null = null;
  version = 0;

  calculatePrescriptionLimit(
    params: CalculatePrescriptionLimitParams,
    options: CalculatePrescriptionLimitOptions = {}
  ): Result {
    const validationError = this.validateParams(params);
    if (validationError) return validationError;

    const { seedRate, isOutsideRange } = this.getSeedRate(params.selectedCrop, params.seedingDate!);
    if (isOutsideRange && !options.allowDateOutsideRange) {
      return { ok: false, reason: 'date_outside_range' };
    }

    if (isCropHybrid(params.selectedCrop)) {
      return {
        ok: true,
        value: this.calculatePrescriptionLimitForHybridCrop(
          params.unitsPerSack!,
          params.sackWeight!,
          seedRate,
          params.organic,
          params.climate
        ),
      };
    }

    return {
      ok: true,
      value: this.calculatePrescriptionLimitForNonHybridCrop(
        params.TGW!,
        params.germinationPercentage!,
        params.seedbedQuality,
        params.soilType,
        seedRate,
        params.organic,
        params.climate
      ),
    };
  }

  private validateParams(params: CalculatePrescriptionLimitParams): ErrorResult | null {
    if (!params.selectedCrop || params.selectedCrop === CropType.NONE || !params.seedingDate) {
      return { ok: false, reason: 'missing_params' };
    }

    const hybrid = isCropHybrid(params.selectedCrop);
    const required = hybrid ? requiredForHybrid : requiredForNonHybrid;
    const missing = required.filter((key) => params[key] == null || params[key] === '');
    if (missing.length) {
      return { ok: false, reason: 'missing_params: ' + missing.join(', ') };
    }

    const positive = hybrid ? positiveForHybrid : positiveForNonHybrid;
    const nonPositive = positive.filter((key) => Number(params[key]) <= 0);
    if (nonPositive.length) {
      return {
        ok: false,
        reason: 'following_params_should_be_positive: ' + nonPositive.join(', '),
      };
    }

    if (!hybrid && params.germinationPercentage != null && params.germinationPercentage < 50) {
      return { ok: false, reason: 'germination_percentage_below_minimum' };
    }

    if (Number.isNaN(params.seedingDate.getTime())) {
      throw new Error('Invalid date');
    }

    return null;
  }

  protected getSeedRate(crop: Crop, date: Date): SeedRateResult {
    throw new Error('Not implemented');
  }

  protected calculatePrescriptionLimitForHybridCrop(
    unitsPerSack: number,
    sackWeight: number,
    seedRate: number,
    organic: boolean,
    climate: Climate
  ): number {
    throw new Error('Not implemented');
  }

  protected calculatePrescriptionLimitForNonHybridCrop(
    TGW: number,
    germinationPercentage: number,
    seedbedQuality: SeedbedQuality,
    soilType: SoilType,
    seedRate: number,
    organic: boolean,
    climate: Climate
  ): number {
    throw new Error('Not implemented');
  }

  protected seedbedQualityToPercentage(seedbedQuality: SeedbedQuality): number {
    throw new Error('Not implemented');
  }

  protected climateToPercentage(climate: Climate): number {
    throw new Error('Not implemented');
  }

  protected soilTypeToPercentage(soilType: SoilType): number {
    throw new Error('Not implemented');
  }
}
